import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Currency, type EventRevisionPatchDto, type SourcingEventDto } from "../../../autogen/bff-api";
import type { FieldError } from "../../../common/editing/save-coordinator/types";
import { useFieldErrorTranslation } from "../../../common/editing/save-coordinator/useFieldErrorTranslation";
import { displaySum } from "../../../common/formatting/displaySum";
import { NumericInput } from "../../../common/input/NumericInput";
import { isFrameworkEnabled } from "../../../common/procurement/publicProcurementOptions";

export type ChangeValueField = "estimatedValue" | "frameworkMaximumValue";

export const CHANGE_VALUE_FIELDS: readonly string[] = ["estimatedValue", "frameworkMaximumValue"];

const INPUT_IDS: Record<ChangeValueField, string> = {
  estimatedValue: "change-estimated-value",
  frameworkMaximumValue: "change-framework-maximum-value",
};

export const focusChangeValueField = (within: HTMLElement | null, field: string) =>
  within?.querySelector<HTMLElement>(`#${INPUT_IDS[field as ChangeValueField]}`)?.focus();

interface Props {
  event: SourcingEventDto;
  estimatedValue: number | null | undefined;
  frameworkMaximumValue: number | null | undefined;
  isSaving: (field: ChangeValueField) => boolean;
  errorOf: (field: ChangeValueField) => FieldError | undefined;
  onChange: (patch: EventRevisionPatchDto) => void;
}

export const ChangeValueFields = ({
  event,
  estimatedValue,
  frameworkMaximumValue,
  isSaving,
  errorOf,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const translateError = useFieldErrorTranslation();
  if (event.eventContext.type !== "Public") return null;
  const publicContext = event.eventContext;
  const currency =
    publicContext.estimatedValue?.currency ?? publicContext.frameworkMaximumValue?.currency ?? Currency.Nok;
  const isFramework = isFrameworkEnabled(publicContext.frameworkAgreement);
  const announcedEstimate = publicContext.estimatedValue?.value ?? null;
  const announcedMaximum = publicContext.frameworkMaximumValue?.value ?? null;
  const shownEstimate = estimatedValue ?? announcedEstimate;
  const shownMaximum = frameworkMaximumValue ?? announcedMaximum;
  const maximumBelowEstimate: FieldError | undefined =
    shownEstimate != null && shownMaximum != null && shownMaximum < shownEstimate
      ? {
          code: "zz_FieldErrorCode_FrameworkMaximumValueBelowEstimate",
          params: { amount: displaySum({ value: shownEstimate, currency }) },
        }
      : undefined;
  const estimateError = errorOf("estimatedValue");
  const maximumError = errorOf("frameworkMaximumValue") ?? maximumBelowEstimate;
  const partHelp = (
    <FormHelperText>
      {t(
        "The competition keeps the part of the regulations it was announced under. Raising the value past that part's upper limit needs a new announcement.",
      )}
    </FormHelperText>
  );
  const keptHelp = (announced: number | null) =>
    announced != null && (
      <FormHelperText>
        {t("Left empty, the announced value stays unchanged: {{amount}}", {
          amount: displaySum({ value: announced, currency }),
        })}
      </FormHelperText>
    );

  return (
    <Stack spacing="5">
      <FormControl isInvalid={estimateError != null}>
        <FormLabel htmlFor={INPUT_IDS.estimatedValue}>
          {t("Estimated value")} {isSaving("estimatedValue") && <Spinner size="xs" />}
        </FormLabel>
        <InputGroup maxW={{ base: "full", sm: "280px" }}>
          <NumericInput
            id={INPUT_IDS.estimatedValue}
            numericValue={estimatedValue ?? null}
            onNumericChange={(value) => onChange({ estimatedValue: value })}
            placeholder={announcedEstimate != null ? displaySum({ value: announcedEstimate, currency }) : undefined}
            min={1}
          />
          <InputRightAddon>{currency}</InputRightAddon>
        </InputGroup>
        {estimateError && <FormErrorMessage>{translateError(estimateError)}</FormErrorMessage>}
        {estimatedValue == null && keptHelp(announcedEstimate)}
        {!isFramework && partHelp}
      </FormControl>
      {isFramework && (
        <FormControl isInvalid={maximumError != null}>
          <FormLabel htmlFor={INPUT_IDS.frameworkMaximumValue}>
            {t("Maximum value")} {isSaving("frameworkMaximumValue") && <Spinner size="xs" />}
          </FormLabel>
          <InputGroup maxW={{ base: "full", sm: "280px" }}>
            <NumericInput
              id={INPUT_IDS.frameworkMaximumValue}
              numericValue={frameworkMaximumValue ?? null}
              onNumericChange={(value) => onChange({ frameworkMaximumValue: value })}
              placeholder={announcedMaximum != null ? displaySum({ value: announcedMaximum, currency }) : undefined}
              min={1}
            />
            <InputRightAddon>{currency}</InputRightAddon>
          </InputGroup>
          {maximumError && <FormErrorMessage>{translateError(maximumError)}</FormErrorMessage>}
          {frameworkMaximumValue == null && keptHelp(announcedMaximum)}
          {partHelp}
        </FormControl>
      )}
    </Stack>
  );
};
